import { IconStatus, Shadow, TableItem, Text, Tooltip, HStack, VStack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { ResponsesStatementResponse } from '@genial/services/dist/models';
import { Utils, useBreakpointValue } from '@genial/utils';
import { Box } from 'native-base';
import React from 'react';

import { currentAccountWidthCols } from '../../../../constants';

import { getTypeTransaction, messageError } from './constants';

interface CurrentAccountItemProps {
  data: ResponsesStatementResponse;
}

export const CurrentAccountItem: React.FC<CurrentAccountItemProps> = ({ data }) => {
  const { icon, bgColor, iconColor } = Utils.Status.Order.GetFromServerStatus(
    data?.transactionStatusId || 1
  );

  const transactionValueType = getTypeTransaction(data.transactionValue);
  const transactionDescriptionValue = data?.transactionDescription || messageError;
  const transactionValueColor =
    data.transactionValue < 0 ? 'feedback.error.default' : 'feedback.success.default';
  const transactionValueFormatted = Utils.Mask.Money(data.transactionValue || 0);
  const requestedDateFormatted = data?.requestDate
    ? Utils.Mask.DateFormat(data?.requestDate, 'dd/MM/yyyy')
    : messageError;

  const settlementDateFormatted = data?.settlementDate
    ? Utils.Mask.DateFormat(data?.settlementDate, 'dd/MM/yyyy')
    : messageError;

  const isMobile = useBreakpointValue({
    base: true,
    sm: false,
  });

  const isTablet = useBreakpointValue({
    base: false,
    sm: true,
    lg: false,
  });

  if (isMobile) {
    return (
      <Box marginBottom={Spacing['inset.xs']}>
        <Shadow level={2} borderRadius={BoxRadius.sm} mx={Spacing['inline.nano']}>
          <VStack
            p={Spacing['inline.xxxs']}
            space={Spacing['inline.nano']}
            borderRadius={BoxRadius.md}>
            <HStack justifyContent="space-between" alignItems="center" w="full">
              <Text fontSize={Typography['fontsize.md']}>Data de solicitação</Text>
              <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
                {requestedDateFormatted}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" alignItems="center" w="full">
              <Text fontSize={Typography['fontsize.md']}>Data de liquidação</Text>
              <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
                {settlementDateFormatted}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" alignItems="center" w="full">
              <Text fontSize={Typography['fontsize.md']}>Descrição</Text>
              <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
                {transactionValueType.desktop}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" alignItems="center" w="full">
              <Text fontSize={Typography['fontsize.md']}>Valor(R$)</Text>
              <Text
                fontSize={Typography['fontsize.md']}
                color={transactionValueColor}
                variant="semibold">
                {transactionValueFormatted}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" alignItems="center" w="full">
              <Text fontSize={Typography['fontsize.md']}>Saldo (R$)</Text>
              <Text fontSize={Typography['fontsize.md']} color="neutral.dark">
                {Utils.Mask.Money(data.balanceAfterTransaction || 0)}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" alignItems="center" w="full">
              <Text fontSize={Typography['fontsize.md']}>Status</Text>
              <HStack space={Spacing['inset.quarck']} alignItems="center">
                <IconStatus
                  hiddenIconSize="xs"
                  size="xxxxs"
                  icons={[
                    {
                      icon,
                      color: iconColor,
                      bgColor,
                      size: 'xxxs',
                    },
                  ]}
                  limit={3}
                  testId="status-icons"
                />
                <Text fontSize={Typography['fontsize.md']} variant="regular" noOfLines={1}>
                  {data?.transactionStatus || messageError}
                </Text>
              </HStack>
            </HStack>
          </VStack>
        </Shadow>
      </Box>
    );
  }

  if (isTablet) {
    return (
      <Shadow marginBottom={Spacing['inset.nano']} level={3} borderRadius={BoxRadius.sm}>
        <VStack space={Spacing['inline.nano']} borderRadius={BoxRadius.sm}>
          <HStack
            alignItems={{ base: 'flex-start', lg: 'center' }}
            justifyContent="space-between"
            py={Spacing['inline.xxxs']}
            px={{ base: 0, lg: Spacing['inline.quarck'] }}>
            <VStack
              space={Spacing['inline.quarck']}
              px={{ base: 0, sm: Spacing['inline.xxs'] }}
              flex={1}>
              <Tooltip label={`Data de solicitação: ${requestedDateFormatted}`}>
                <Box>
                  <Text
                    fontSize={Typography['fontsize.md']}
                    variant={'semibold'}
                    display={{ base: 'unset', lg: 'none' }}
                    noOfLines={1}>
                    Data de solicitação
                  </Text>

                  <Text
                    fontSize={Typography['fontsize.md']}
                    variant={isTablet ? 'regular' : 'semibold'}
                    noOfLines={2}>
                    {requestedDateFormatted}
                  </Text>
                </Box>
              </Tooltip>
            </VStack>
            <VStack
              space={Spacing['inline.quarck']}
              px={{ base: 0, sm: Spacing['inline.xxs'] }}
              flex={1}>
              <Tooltip label={`Data de liquidação: ${settlementDateFormatted}`}>
                <Box>
                  <Text
                    fontSize={Typography['fontsize.md']}
                    variant={'semibold'}
                    noOfLines={1}
                    display={{ base: 'unset', lg: 'none' }}>
                    Data de liquidação
                  </Text>
                  <Text
                    fontSize={Typography['fontsize.md']}
                    variant={isTablet ? 'regular' : 'semibold'}
                    noOfLines={2}>
                    {settlementDateFormatted}
                  </Text>
                </Box>
              </Tooltip>
            </VStack>
            <VStack
              space={Spacing['inline.quarck']}
              px={{ base: 0, sm: Spacing['inline.xxs'] }}
              flex={1}>
              <Text fontSize={Typography['fontsize.md']} variant="semibold">
                {transactionValueType.desktop}
              </Text>
              <Tooltip label={transactionDescriptionValue}>
                <Box>
                  <Text fontSize={Typography['fontsize.md']} noOfLines={2} variant="regular">
                    {transactionDescriptionValue}
                  </Text>
                </Box>
              </Tooltip>
            </VStack>
          </HStack>

          <HStack
            alignItems={{ base: 'flex-start', lg: 'center' }}
            justifyContent="space-between"
            py={Spacing['inline.xxxs']}
            px={{ base: 0, lg: Spacing['inline.quarck'] }}>
            <VStack
              space={Spacing['inline.quarck']}
              px={{ base: 0, sm: Spacing['inline.xxs'] }}
              flex={1}>
              <Text
                fontSize={Typography['fontsize.md']}
                variant={'semibold'}
                display={{ base: 'unset', lg: 'none' }}>
                Valor
              </Text>
              <Tooltip label={transactionValueFormatted}>
                <Box>
                  <Text
                    noOfLines={2}
                    fontSize={Typography['fontsize.md']}
                    variant="semibold"
                    color={transactionValueColor}>
                    {transactionValueFormatted}
                  </Text>
                </Box>
              </Tooltip>
            </VStack>
            <VStack
              space={Spacing['inline.quarck']}
              px={{ base: 0, sm: Spacing['inline.xxs'] }}
              flex={1}>
              <Text
                fontSize={Typography['fontsize.md']}
                variant={'semibold'}
                display={{ base: 'unset', lg: 'none' }}>
                Saldo
              </Text>
              <Tooltip label={Utils.Mask.Money(data.balanceAfterTransaction || 0)}>
                <Box>
                  <Text fontSize={Typography['fontsize.md']} variant="regular" noOfLines={2}>
                    {Utils.Mask.Money(data.balanceAfterTransaction || 0)}
                  </Text>
                </Box>
              </Tooltip>
            </VStack>
            <VStack
              space={Spacing['inline.quarck']}
              px={{ base: 0, sm: Spacing['inline.xxs'] }}
              flex={1}>
              <Text
                fontSize={Typography['fontsize.md']}
                variant={'semibold'}
                display={{ base: 'unset', lg: 'none' }}>
                Status
              </Text>
              <Tooltip label={data?.transactionStatus?.toUpperCase() || messageError}>
                <HStack space={Spacing['inset.quarck']} alignItems="center">
                  <IconStatus
                    hiddenIconSize="xs"
                    size="xxxxs"
                    icons={[
                      {
                        icon,
                        color: iconColor,
                        bgColor,
                        size: 'xxxs',
                      },
                    ]}
                    limit={3}
                    testId="status-icons"
                  />
                  <Text fontSize={Typography['fontsize.md']} variant="regular" noOfLines={2}>
                    {data?.transactionStatus?.toUpperCase() || messageError}
                  </Text>
                </HStack>
              </Tooltip>
            </VStack>
          </HStack>
        </VStack>
      </Shadow>
    );
  }

  return (
    <Shadow marginBottom={Spacing['inset.nano']} level={3} borderRadius={BoxRadius.sm}>
      <HStack
        alignItems={{ base: 'flex-start', lg: 'center' }}
        justifyContent="space-between"
        py={Spacing['inline.xxxs']}
        px={{ base: 0, lg: Spacing['inline.quarck'] }}>
        <TableItem
          testId="statement-current-account-requested-date-item"
          width={currentAccountWidthCols.requestedDate.desktop}>
          <Tooltip label={`Data de solicitação: ${requestedDateFormatted}`}>
            <Box>
              <Text
                fontSize={Typography['fontsize.md']}
                variant={'semibold'}
                display={{ base: 'unset', lg: 'none' }}
                noOfLines={1}>
                Data de solicitação
              </Text>

              <Text
                fontSize={Typography['fontsize.md']}
                variant={isTablet ? 'regular' : 'semibold'}
                noOfLines={2}>
                {requestedDateFormatted}
              </Text>
            </Box>
          </Tooltip>
        </TableItem>
        <TableItem
          testId="statement-current-account-settlement-date-item"
          width={currentAccountWidthCols.settlementDate.desktop}>
          <Tooltip label={`Data de liquidação: ${settlementDateFormatted}`}>
            <Box>
              <Text
                fontSize={Typography['fontsize.md']}
                variant={'semibold'}
                noOfLines={1}
                display={{ base: 'unset', lg: 'none' }}>
                Data de liquidação
              </Text>
              <Text
                fontSize={Typography['fontsize.md']}
                variant={isTablet ? 'regular' : 'semibold'}
                noOfLines={2}>
                {settlementDateFormatted}
              </Text>
            </Box>
          </Tooltip>
        </TableItem>
        <TableItem
          width={currentAccountWidthCols.description.desktop}
          testId="statement-current-account-description-date-item">
          <Text fontSize={Typography['fontsize.md']} variant="semibold">
            {transactionValueType.desktop}
          </Text>
          <Tooltip label={transactionDescriptionValue}>
            <Box>
              <Text fontSize={Typography['fontsize.md']} noOfLines={2} variant="regular">
                {transactionDescriptionValue}
              </Text>
            </Box>
          </Tooltip>
        </TableItem>
        <TableItem
          width={currentAccountWidthCols.amountValue.desktop}
          testId="statement-current-account-amount-value-item">
          <Text
            fontSize={Typography['fontsize.md']}
            variant={'semibold'}
            display={{ base: 'unset', lg: 'none' }}>
            Valor
          </Text>
          <Tooltip label={transactionValueFormatted}>
            <Box>
              <Text
                noOfLines={2}
                fontSize={Typography['fontsize.md']}
                variant="semibold"
                color={transactionValueColor}>
                {transactionValueFormatted}
              </Text>
            </Box>
          </Tooltip>
        </TableItem>
        <TableItem
          width={currentAccountWidthCols.availableBalance.desktop}
          testId="statement-current-account-available-balance-item">
          <Text
            fontSize={Typography['fontsize.md']}
            variant={'semibold'}
            display={{ base: 'unset', lg: 'none' }}>
            Saldo
          </Text>
          <Tooltip label={Utils.Mask.Money(data.balanceAfterTransaction || 0)}>
            <Box>
              <Text fontSize={Typography['fontsize.md']} variant="regular" noOfLines={2}>
                {Utils.Mask.Money(data.balanceAfterTransaction || 0)}
              </Text>
            </Box>
          </Tooltip>
        </TableItem>
        <TableItem
          width={currentAccountWidthCols.status.desktop}
          testId="statement-current-account-status-item">
          <Text
            fontSize={Typography['fontsize.md']}
            variant={'semibold'}
            display={{ base: 'unset', lg: 'none' }}>
            Status
          </Text>
          <Tooltip label={data?.transactionStatus?.toUpperCase() || messageError}>
            <HStack space={Spacing['inset.quarck']} alignItems="center">
              <IconStatus
                hiddenIconSize="xs"
                size="xxxxs"
                icons={[
                  {
                    icon,
                    color: iconColor,
                    bgColor,
                    size: 'xxxs',
                  },
                ]}
                limit={3}
                testId="status-icons"
              />
              <Text fontSize={Typography['fontsize.md']} variant="regular" noOfLines={2}>
                {data?.transactionStatus?.toUpperCase() || messageError}
              </Text>
            </HStack>
          </Tooltip>
        </TableItem>
      </HStack>
    </Shadow>
  );
};
